import { useQuery } from "@tanstack/react-query"
import { InvitationExpiredPage } from "../pages/InvitationExpiredPage"
import { SomethingWentWrongPage } from "../../../components/SomethingWentWrongPage"
import { TooManyRequestsPage } from "../pages/TooManyRequestsPage"
import { fetchInvitation } from "../api"
import { ApiResponseError, isNotFound } from "@rawa/api-client/ApiResponseError"
import { invitationQueryKey } from "../utils/invitationQueryKey"
import { InvitationContext } from "./InvitationContext"

type Props = {
  invitationId: string
  children: React.ReactNode
  kioskMode?: boolean
}

/**
 * Lets a client in on a valid invitation, and explains why not otherwise.
 *
 * The answer is asked for once and kept for the visit (ADR-031): the welcome
 * page and the form each have a gate, and the second draws from the first's
 * answer with no blank and no second request. It is not asked again when the
 * client comes back to the page or the connection returns — the check matters on
 * the way in, submitting is the real one, and asking mid-form could only take
 * the form, and its answers, away. For the same reason an answer already given
 * outranks a later failure. Sending the feedback forgets the invitation, so going
 * back to it afterwards asks again and finds it used.
 *
 * Retries are the query client's: a dropped connection is ridden out, while an
 * expired link (404) or a rate limit (429) is shown at once. Those are answers,
 * and not reported; anything else is.
 */
export function InvitationGate({ invitationId, children, kioskMode }: Props) {
  const { data, error, isPending } = useQuery({
    queryKey: invitationQueryKey(invitationId),
    queryFn: () => fetchInvitation(invitationId),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    meta: { source: "fetchInvitation", clientErrorsAreExpected: true },
  })

  if (data)
    return (
      <InvitationContext.Provider value={{ firstName: data.firstName }}>
        {children}
      </InvitationContext.Provider>
    )
  if (isPending) return null
  if (error instanceof ApiResponseError && error.statusCode === 429)
    return <TooManyRequestsPage kioskMode={kioskMode} />
  if (isNotFound(error)) return <InvitationExpiredPage kioskMode={kioskMode} />
  // A 5xx, a dropped connection or an unreadable reply are not the client's
  // link being invalid — telling them it expired sends them away from a link
  // that still works.
  return <SomethingWentWrongPage kioskMode={kioskMode} />
}
